import { useMemo, useCallback, useState, useEffect, useRef, Activity } from 'react';

import useLoadSpeakers from '@/connection/hooks/use-load-speakers';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { selectSessionByUuid } from '@/store/slices/project/selectors';
import { selectSpeakers } from '@/store/slices/speakers/selectors';
import { Speaker } from '@/types/speakers.types';
import CustomSelect from '@ui/select/custom-select/custom-select';
import { TReactSelectOption } from '@ui/select/react-select/custom-react-select.types';
import RenderUnfocusedLabelContent from './components/render-unfocused-label-content';
import RenderFocusedLabelContent from './components/render-focused-label-content';
import RenderMenuOptionContent from './components/render-menu-option-content';
import RenderNoOptionsContent from './components/render-no-options-content';
import saveUpdatedCast from '../helpers/save-updated-cast';
import InviteCastModal from './components/invite-cast-modal/invite-cast-modal';
import RenderHeaderLabelContent from './components/render-header-label-content';
import { setNewSpeakerIntoSlice } from '@/store/slices/speakers';
import { selectChannelAdmins } from '@/store/slices/channel-admins/selectors';
import useSpeakersForSession from '@/utils/speakers/use-speakers-for-session';

const CastAndCrewSelectWithInvites = ({
  sessionUuid,
  setCrewSelectActive,
  hideSelectHeader,
  autoFocus,
  maskOverflowColor,
  onFocusClosed: propsOnFocusClosed,
  onChange: propsOnChange,
  hideDefaultOptionsFromSelectedList,
  hidden,
}: {
  sessionUuid: string;
  setCrewSelectActive?: (isActive: boolean) => void;
  hideSelectHeader?: boolean;
  autoFocus?: boolean;
  maskOverflowColor?: string;
  onFocusClosed?: (selectedOptions: TReactSelectOption[]) => void;
  onChange?: (selectedOptions: TReactSelectOption[]) => void;
  hideDefaultOptionsFromSelectedList?: boolean;
  hidden?: boolean;
}) => {
  useLoadSpeakers({ skipIfSpeakersExist: true });

  const inputRef = useRef<HTMLInputElement | null>(null);
  const invisibleTriggerRef = useRef<HTMLSpanElement | null>(null);

  const dispatch = useAppDispatch();

  const speakers = useAppSelector(selectSpeakers);
  const session = useAppSelector(selectSessionByUuid(sessionUuid));
  const channelAdmins = useAppSelector(selectChannelAdmins);

  const [showInviteModal, setShowInviteModal] = useState(false);
  const [lockActiveState, setLockActiveState] = useState(false);
  const [initialName, setInitialName] = useState('');

  const sessionSpeakers = useSpeakersForSession({ sessionUuid });

  const [controlledOptions, setControlledOptions] = useState<TReactSelectOption[]>([]);

  const { defaultOptions, defaultOptionsMap } = useMemo(() => {
    const opts = sessionSpeakers
      .map((s) => {
        if (!s) return null;
        return {
          label: `${s.first_name} ${s.last_name} ${s?.email || ''}`.trim(),
          value: s.speaker,
          meta: s,
        };
      })
      .filter(Boolean) as TReactSelectOption<string, number, Speaker>[];

    return {
      defaultOptions: opts,
      defaultOptionsMap: Object.fromEntries(opts.map((opt) => [opt.value, opt])),
    };
  }, [sessionSpeakers]);

  useEffect(() => {
    if (hideDefaultOptionsFromSelectedList) return;
    setControlledOptions(defaultOptions);
  }, [defaultOptions, hideDefaultOptionsFromSelectedList]);

  const options = useMemo(() => {
    return (speakers || []).reduce(
      (accumulatedOptions: TReactSelectOption<string, number, Speaker>[], option: Speaker) => {
        // don't show already selected options in the menu dropdown if we are hiding defaults from the UI
        // this is to avoid selecting the same speaker twice.
        if (hideDefaultOptionsFromSelectedList && defaultOptionsMap[option.speaker]) {
          return accumulatedOptions;
        }

        const label = `${option.first_name} ${option.last_name} ${option?.email || ''}`.trim();
        accumulatedOptions.push({
          label,
          value: option.speaker,
          meta: option,
        });
        return accumulatedOptions;
      },
      []
    );
  }, [defaultOptionsMap, hideDefaultOptionsFromSelectedList, speakers]);

  const onFocusClosed = useCallback(
    async (selectedOptions: TReactSelectOption[]) => {
      await saveUpdatedCast({
        session,
        defaultOptions,
        selectedOptions,
        dispatch,
      });
    },
    [defaultOptions, dispatch, session]
  );

  const handleNewCast = (speaker: Speaker) => {
    const matchedAdmin = speaker?.email ? channelAdmins?.find((admin) => admin.email === speaker.email) || null : null;
    const newSpeaker: Speaker = { ...speaker, bl_admin: matchedAdmin || speaker.bl_admin };
    const existingOptions = [...(hideDefaultOptionsFromSelectedList ? defaultOptions : [])];
    // do not add controlled options here, because they only get saved when clicking the "add" button
    onFocusClosed([
      ...existingOptions,
      {
        label: `${newSpeaker.first_name} ${newSpeaker.last_name} ${newSpeaker?.email || ''}`.trim(),
        value: newSpeaker.speaker,
        meta: newSpeaker,
      },
    ]);
    dispatch(setNewSpeakerIntoSlice(newSpeaker));
    setShowInviteModal(false);
  };

  const handleChange = useCallback(
    (options: TReactSelectOption[]) => {
      setControlledOptions(options);
      propsOnChange?.(options);
    },
    [propsOnChange]
  );

  const onInviteModalRequestClose = useCallback(() => {
    setShowInviteModal(false);
  }, []);

  const onInviteCastModalTransitionClosed = useCallback(() => {
    invisibleTriggerRef?.current?.click();
    setLockActiveState(false);
  }, []);

  const handleOnFocusClosed = useCallback(
    (selectedOptions: TReactSelectOption[]) => {
      const newOptions = [...(hideDefaultOptionsFromSelectedList ? defaultOptions : []), ...selectedOptions];
      if (propsOnFocusClosed) return propsOnFocusClosed(newOptions);
      return onFocusClosed(selectedOptions);
    },
    [defaultOptions, hideDefaultOptionsFromSelectedList, onFocusClosed, propsOnFocusClosed]
  );

  return (
    <Activity mode={hidden ? 'hidden' : 'visible'}>
      <CustomSelect
        autoFocus={autoFocus}
        lockActiveState={lockActiveState}
        onAddNewSelect={(inputValue) => {
          setLockActiveState(true);
          setShowInviteModal(true);
          setInitialName(inputValue);
        }}
        inputRef={inputRef}
        onFocusChange={setCrewSelectActive}
        id="cast-and-crew-select-with-invites"
        placeholder="Add members"
        options={options}
        controlledOptions={controlledOptions}
        onFocusClosed={handleOnFocusClosed}
        onChange={handleChange}
        hideHeader={hideSelectHeader}
        RenderHeaderLabelContent={RenderHeaderLabelContent}
        RenderUnfocusedLabelContent={({ option, idx }) => {
          return <RenderUnfocusedLabelContent option={option} idx={idx} sessionUuid={sessionUuid} />;
        }}
        RenderFocusedLabelContent={RenderFocusedLabelContent}
        RenderMenuOptionContent={RenderMenuOptionContent}
        RenderNoOptionsContent={RenderNoOptionsContent}
        maskOverflowColor={maskOverflowColor}
        invisibleTriggerRef={invisibleTriggerRef}
      />

      <InviteCastModal
        open={showInviteModal}
        initialName={initialName}
        onClose={onInviteModalRequestClose}
        handleNewCast={handleNewCast}
        onTransitionClosed={onInviteCastModalTransitionClosed}
      />
    </Activity>
  );
};

export default CastAndCrewSelectWithInvites;
